import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchAdminTenants } from '../services/adminApi';
import TenantDetail from '../components/tenants/TenantDetail';
import { MODULES, BILLABLE_MODULE_KEYS, BILLABLE_MODULE_PLAN_KEYS } from '../../config/modules';
import { WASTE_PLAN_LIMITS, PPWR_PLAN_LIMITS } from '../../config/plans';

const TYPE_LABELS = {
    company: 'Spoločnosť',
    school: 'Škola',
    town: 'Obec',
    transporter: 'Prepravca',
    'recycling center': 'Zberný dvor',
};

const MODULE_LABELS_SHORT = { waste: 'Odpady', ppwr: 'PPWR' };
const MODULE_PLAN_LIMITS = { waste: WASTE_PLAN_LIMITS, ppwr: PPWR_PLAN_LIMITS };
const planLabel = (module, plan) => MODULE_PLAN_LIMITS[module]?.[plan]?.label ?? plan;

const MODULE_STATUS_COLOR = {
    trialing: 'yellow',
    active: 'green',
    past_due: 'red',
    canceled: 'gray',
};

const MODULE_FILTER_OPTIONS = [
    { value: '', label: 'Všetky balíky' },
    ...BILLABLE_MODULE_KEYS.map(module => ({ value: module, label: MODULES[module] })),
];

const FILTER_PLAN_OPTIONS = Object.fromEntries(
    BILLABLE_MODULE_KEYS.map(module => [
        module,
        BILLABLE_MODULE_PLAN_KEYS[module].map(plan => ({ value: plan, label: planLabel(module, plan) })),
    ])
);

const FILTER_STATUS_OPTIONS = [
    { value: 'trialing', label: 'Skúšobné' },
    { value: 'active', label: 'Aktívne' },
    { value: 'past_due', label: 'Po splatnosti' },
    { value: 'canceled', label: 'Zrušené' },
];

const STATUS_META = {
    trialing:  { label: 'Skúšobné',     color: 'yellow' },
    active:    { label: 'Aktívne',       color: 'green'  },
    past_due:  { label: 'Po splatnosti', color: 'red'    },
    canceled:  { label: 'Zrušené',       color: 'gray'   },
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

const PAGE_SIZE = 20;
const EXPIRING_SOON_DAYS = 30;

export default function Tenants() {
    const [searchParams] = useSearchParams();
    const [tenants, setTenants] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [moduleFilter, setModuleFilter] = useState(() => searchParams.get('module') ?? '');
    const [planFilter, setPlanFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expiringOnly, setExpiringOnly] = useState(() => searchParams.get('expiringDays') === String(EXPIRING_SOON_DAYS));
    const [page, setPage] = useState(1);
    const [selectedId, setSelectedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const expiringParam = expiringOnly ? { expiringDays: EXPIRING_SOON_DAYS } : {};
            const moduleParams = moduleFilter === 'ppwr'
                ? { module: moduleFilter, ppwrPlan: planFilter, ppwrStatus: statusFilter, ...expiringParam }
                : moduleFilter === 'waste'
                    ? { module: moduleFilter, plan: planFilter, status: statusFilter, ...expiringParam }
                    : {};
            const res = await fetchAdminTenants({
                search, ...moduleParams,
                page, limit: PAGE_SIZE,
            });
            setTenants(res.data.data);
            setTotal(res.data.total);
            setPages(res.data.pages);
        } catch {
            setError('Nepodarilo sa načítať subjekty.');
        } finally {
            setLoading(false);
        }
    }, [search, moduleFilter, planFilter, statusFilter, expiringOnly, page]);

    useEffect(() => {
        const timer = setTimeout(load, search ? 400 : 0);
        return () => clearTimeout(timer);
    }, [load, search]);

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPage(1);
    };

    const handleModuleFilterChange = (e) => {
        setModuleFilter(e.target.value);
        setPlanFilter('');
        setStatusFilter('');
        setExpiringOnly(false);
        setPage(1);
    };

    const handleExpiringOnlyChange = (e) => {
        setExpiringOnly(e.target.checked);
        setPage(1);
    };

    return (
        <>
        <div className="adm-page">
            <div className="adm-page-header">
                <h1 className="adm-page-title">Subjekty</h1>
                <p className="adm-page-subtitle">Zoznam všetkých registrovaných subjektov.</p>
            </div>

            <div className="adm-filters">
                <input
                    className="adm-filter-input"
                    placeholder="Hľadať podľa názvu, e-mailu alebo IČO..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                <select
                    className="adm-filter-select"
                    value={moduleFilter}
                    onChange={handleModuleFilterChange}
                >
                    {MODULE_FILTER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>Balík: {opt.label}</option>
                    ))}
                </select>
                <select
                    className="adm-filter-select"
                    value={planFilter}
                    onChange={handleFilterChange(setPlanFilter)}
                    disabled={!moduleFilter}
                    title={!moduleFilter ? 'Najprv vyberte balík' : undefined}
                >
                    <option value="">Všetky plány</option>
                    {(FILTER_PLAN_OPTIONS[moduleFilter] ?? []).map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    className="adm-filter-select"
                    value={statusFilter}
                    onChange={handleFilterChange(setStatusFilter)}
                    disabled={!moduleFilter}
                    title={!moduleFilter ? 'Najprv vyberte balík' : undefined}
                >
                    <option value="">Všetky stavy</option>
                    {FILTER_STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <label
                    className="adm-filter-checkbox"
                    title={!moduleFilter ? 'Najprv vyberte balík' : undefined}
                >
                    <input
                        type="checkbox"
                        checked={expiringOnly}
                        onChange={handleExpiringOnlyChange}
                        disabled={!moduleFilter}
                    />
                    Expiruje do {EXPIRING_SOON_DAYS} dní
                </label>
            </div>

            {error && <div className="adm-error" style={{ marginBottom: 20 }}>{error}</div>}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
                    <span className="adm-spinner" />
                </div>
            ) : tenants.length === 0 ? (
                <div className="adm-empty">Žiadne subjekty nevyhovujú filtrom.</div>
            ) : (
                <div className="adm-table-wrap">
                    <table className="adm-table">
                        <thead>
                            <tr>
                                <th>Spoločnosť</th>
                                <th>E-mail</th>
                                <th>Typ</th>
                                <th>IČO</th>
                                <th>Plán</th>
                                <th>Stav</th>
                                <th>Platná do</th>
                                <th>Moduly</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map(t => {
                                const status = STATUS_META[t.subscriptionStatus] ?? { label: t.subscriptionStatus, color: 'gray' };
                                const isExpired = t.licenseEnd && new Date(t.licenseEnd) < new Date();
                                return (
                                    <tr
                                        key={t._id}
                                        className={`adm-row--clickable${t.blocked ? ' adm-row--blocked' : ''}${selectedId === t._id ? ' adm-row--active' : ''}`}
                                        onClick={() => setSelectedId(t._id)}
                                    >
                                        <td className="adm-td-primary">
                                            {t.companyName}
                                            {t.blocked && <span className="adm-badge adm-badge--red" style={{ marginLeft: 8 }}>Blokovaný</span>}
                                        </td>
                                        <td>{t.email}</td>
                                        <td>{TYPE_LABELS[t.type] ?? t.type}</td>
                                        <td className="adm-td-mono">{t.identificationNumber}</td>
                                        <td>{planLabel('waste', t.plan)}</td>
                                        <td>
                                            <span className={`adm-badge adm-badge--${status.color}`}>
                                                {status.label}
                                            </span>
                                        </td>
                                        <td style={isExpired ? { color: 'var(--adm-red)' } : {}}>
                                            {formatDate(t.licenseEnd)}
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                {Object.entries(t.licenses ?? {}).map(([module, license]) => (
                                                    <span
                                                        key={module}
                                                        className={`adm-badge adm-badge--${license ? (MODULE_STATUS_COLOR[license.status] ?? 'gray') : 'gray'}`}
                                                        style={license ? undefined : { opacity: 0.4 }}
                                                        title={license ? `${planLabel(module, license.plan)} — ${formatDate(license.licenseEnd)}` : 'Balík nie je aktivovaný'}
                                                    >
                                                        {MODULE_LABELS_SHORT[module] ?? module}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="adm-table-footer">
                        <span>{total} {total === 1 ? 'subjekt' : total < 5 ? 'subjekty' : 'subjektov'}</span>
                        {pages > 1 && (
                            <div className="adm-pagination">
                                <button
                                    className="adm-page-btn"
                                    onClick={() => setPage(p => p - 1)}
                                    disabled={page === 1}
                                >
                                    ‹
                                </button>
                                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        className={`adm-page-btn${p === page ? ' adm-page-btn--active' : ''}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button
                                    className="adm-page-btn"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page === pages}
                                >
                                    ›
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>

            {selectedId && (
                <TenantDetail
                    tenantId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onUpdate={load}
                />
            )}
        </>
    );
}
